import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Slider,
  IconButton,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  SkipPrevious as BackIcon,
  SkipNext as ForwardIcon,
  Fullscreen as FullExtentIcon,
} from '@mui/icons-material';
import { PathfinderDataPoint, PathfinderRoute } from '../../services/pathfinderApi';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import MapLayerControlsDialog, { type PathfinderCameraControls } from './MapLayerControlsDialog';

/** Minimal shape for skip-dwells playback (backend dwell rows). */
export interface PathfinderDwellWindow {
  start_time: string;
  end_time: string;
}

interface CompactTimeSliderProps {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  timeRange: { start: Date; end: Date } | null;
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  dwellLocations?: PathfinderDwellWindow[]; // Optional dwell data for skip dwells feature
  onTimeUpdate: (
    time: Date,
    visiblePoints: PathfinderDataPoint[],
    visibleRoutes: PathfinderRoute[],
    isFullExtent: boolean,
    window: { start: Date; end: Date }
  ) => void;
  onFollowModeChange?: (enabled: boolean) => void;
  
  // Layer control props
  showDataPoints?: boolean;
  onShowDataPointsChange?: (show: boolean) => void;
  showRouteLines?: boolean;
  onShowRouteLinesChange?: (show: boolean) => void;
  showDirectionArrows?: boolean;
  onShowDirectionArrowsChange?: (show: boolean) => void;
  showHeatmap?: boolean;
  onShowHeatmapChange?: (show: boolean) => void;
  enableClustering?: boolean;
  onEnableClusteringChange?: (enable: boolean) => void;
  showCoTravelerPoints?: boolean;
  onShowCoTravelerPointsChange?: (show: boolean) => void;
  showCoTravelerLines?: boolean;
  onShowCoTravelerLinesChange?: (show: boolean) => void;
  showCoTravelerHeatmap?: boolean;
  onShowCoTravelerHeatmapChange?: (show: boolean) => void;
  highlightClosestSrp?: boolean;
  onHighlightClosestSrpChange?: (show: boolean) => void;
  showDwells?: boolean;
  onShowDwellsChange?: (show: boolean) => void;
  showPttMessages?: boolean;
  onShowPttMessagesChange?: (show: boolean) => void;
  clusterPttMessages?: boolean;
  onClusterPttMessagesChange?: (cluster: boolean) => void;
  hideGaps?: boolean;
  onHideGapsChange?: (hide: boolean) => void;
  hideImplausibleMovement?: boolean;
  onHideImplausibleMovementChange?: (hide: boolean) => void;
  implausibleMaxSpeedKmh?: number;
  onImplausibleMaxSpeedKmhChange?: (kmh: number) => void;
  onUseOrgTrackFilterDefaults?: () => void;
  trackFiltersUserCustomized?: boolean;
  // White Cell sampling - show one point per interval
  whiteCellInterval?: number | null; // minutes: 2, 15, 30, 60, 120, or null for off
  onWhiteCellIntervalChange?: (interval: number | null) => void;
  // Controlled open-state for the centered Map Settings dialog (layers +
  // playback). PathfinderPage owns the boolean so it can be flipped from
  // either the slider's gear button OR the map's toolbar button.
  mapSettingsOpen?: boolean;
  onOpenMapSettings?: () => void;
  onMapSettingsClose?: () => void;
  /**
   * External scrub: centre the playback window on `time` (leaving full-extent
   * mode if needed) and optionally start playing. `nonce` must change for each
   * request so repeated seeks to the same instant still apply.
   */
  seekRequest?: { time: Date; play?: boolean; nonce: number } | null;
  playbackOnly?: boolean;
  // Map-layer toggles that used to be toolbar buttons, forwarded to MapLayerControlsDialog.
  showDwellLabels?: boolean;
  onShowDwellLabelsChange?: (show: boolean) => void;
  dwellCount?: number;
  showItinerary?: boolean;
  onShowItineraryChange?: (show: boolean) => void;
  hasItineraryData?: boolean;
  showCameras?: boolean;
  onShowCamerasChange?: (show: boolean) => void;
  cameraCount?: number;
  cameraControls?: PathfinderCameraControls | null;
  onZoomToBounds?: (bbox: [number, number, number, number]) => void;
}

const timeUnits = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const intervals = Array.from({ length: 60 }, (_, i) => i + 1);
const playRates = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.5, 2.0, 2.5, 3.0];

const getIntervalDurationMs = (unit: string, value: number): number => value * (
  unit === 'seconds' ? 1000 :
  unit === 'minutes' ? 60000 :
  unit === 'hours' ? 3600000 :
  86400000
);

const CompactTimeSlider: React.FC<CompactTimeSliderProps> = ({
  dataPoints,
  routes,
  timeRange,
  selectedDevices,
  dwellLocations = [] as PathfinderDwellWindow[],
  onTimeUpdate,
  onFollowModeChange,
  showDataPoints = true,
  onShowDataPointsChange,
  showRouteLines = true,
  onShowRouteLinesChange,
  showDirectionArrows = false,
  onShowDirectionArrowsChange,
  showHeatmap = false,
  onShowHeatmapChange,
  enableClustering = false,
  onEnableClusteringChange,
  showCoTravelerPoints = false,
  onShowCoTravelerPointsChange,
  showCoTravelerLines = false,
  onShowCoTravelerLinesChange,
  showCoTravelerHeatmap = false,
  onShowCoTravelerHeatmapChange,
  highlightClosestSrp = false,
  onHighlightClosestSrpChange,
  showDwells = true,
  onShowDwellsChange,
  showPttMessages = true,
  onShowPttMessagesChange,
  clusterPttMessages = true,
  onClusterPttMessagesChange,
  hideGaps = false,
  onHideGapsChange,
  hideImplausibleMovement = true,
  onHideImplausibleMovementChange,
  implausibleMaxSpeedKmh = 160,
  onImplausibleMaxSpeedKmhChange,
  onUseOrgTrackFilterDefaults,
  trackFiltersUserCustomized = false,
  whiteCellInterval = null,
  onWhiteCellIntervalChange,
  mapSettingsOpen = false,
  onOpenMapSettings,
  onMapSettingsClose,
  seekRequest = null,
  playbackOnly = false,
  showDwellLabels,
  onShowDwellLabelsChange,
  dwellCount,
  showItinerary,
  onShowItineraryChange,
  hasItineraryData,
  showCameras,
  onShowCamerasChange,
  cameraCount,
  cameraControls,
  onZoomToBounds,
}: CompactTimeSliderProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeExtent, setTimeExtent] = useState<[number, number]>([0, 100]); // Percentage of total time [start%, end%]
  const [fullTimeExtent, setFullTimeExtent] = useState<{ start: Date; end: Date } | null>(null);
  // Playback settings (Unit / Int / Rate / Loop / Follow / Skip Dwell /
  // GPS Gaps) now live inside the centered MapLayerControlsDialog along
  // with map layer toggles - one consolidated panel, one consistent close
  // mechanism. The gear button on this slider simply asks the parent to
  // open that dialog via `onOpenMapSettings`. See the dialog component
  // header for the longer story on why we landed on a single panel.
  const [showFullExtent, setShowFullExtent] = useState(true); // Start with FULL EXTENT to show all data initially
  
  // Playback settings
  const [timeUnit, setTimeUnit] = useState('seconds');
  const [intervalValue, setIntervalValue] = useState(30);
  const [playRate, setPlayRate] = useState(0.5);
  const [loop, setLoop] = useState(true);
  const [followMode, setFollowMode] = useState(false);
  const [skipDwells, setSkipDwells] = useState(false);
  
  const playbackTimerRef = useRef<number | null>(null);
  const windowSizeRef = useRef<number>(5); // Store window size as percentage (default ~5%)

  const getIntervalWindowPercent = useCallback((extent = fullTimeExtent): number => {
    if (!extent) return 5;
    const totalDurationMs = extent.end.getTime() - extent.start.getTime();
    if (totalDurationMs <= 0) return 100;
    return Math.min(100, (getIntervalDurationMs(timeUnit, intervalValue) / totalDurationMs) * 100);
  }, [fullTimeExtent, intervalValue, timeUnit]);

  // Initialize time extent when data loads - MUST reset to full extent on new data
  useEffect(() => {
    if (timeRange) {
      setFullTimeExtent(timeRange);
      
      // Calculate initial window size based on interval + unit
      const windowSizePercent = getIntervalWindowPercent(timeRange);
      windowSizeRef.current = windowSizePercent;
      
      // CRITICAL: Reset to full extent mode when new data loads
      // This ensures the slider shows ALL data for the new timeframe
      setShowFullExtent(true);
      setTimeExtent([0, 100]);
      setIsPlaying(false); // Stop any active playback
    }
  }, [timeRange]); // Only depend on timeRange - triggers on new data load

  // Track previous interval settings to detect actual changes
  const prevIntervalRef = useRef({ intervalValue, timeUnit });
  
  // Update window size when interval settings change - ONLY recenter when interval/timeUnit actually change
  useEffect(() => {
    if (!fullTimeExtent) return;
    
    const windowSizePercent = getIntervalWindowPercent();
    windowSizeRef.current = windowSizePercent;
    
    // Check if interval settings actually changed (vs just showFullExtent changing)
    const intervalChanged = 
      prevIntervalRef.current.intervalValue !== intervalValue ||
      prevIntervalRef.current.timeUnit !== timeUnit;
    
    // Only recenter window if interval settings actually changed AND we're in window mode
    if (intervalChanged && !showFullExtent) {
      setTimeExtent(prev => {
        const currentCenter = (prev[0] + prev[1]) / 2;
        let newStart = Math.max(0, currentCenter - windowSizePercent / 2);
        let newEnd = Math.min(100, currentCenter + windowSizePercent / 2);
        
        // Adjust if we hit boundaries
        if (newStart === 0) newEnd = Math.min(100, windowSizePercent);
        if (newEnd === 100) newStart = Math.max(0, 100 - windowSizePercent);
        
        return [newStart, newEnd];
      });
    }
    
    // Update previous values
    prevIntervalRef.current = { intervalValue, timeUnit };
  }, [intervalValue, timeUnit, fullTimeExtent, showFullExtent]);

  // External seek (e.g. clicking a PTT caption): centre the window on the time.
  const lastSeekNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!seekRequest || !fullTimeExtent) return;
    if (lastSeekNonceRef.current === seekRequest.nonce) return;
    lastSeekNonceRef.current = seekRequest.nonce;

    const totalMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
    if (totalMs <= 0) return;
    const pct = Math.max(0, Math.min(100, ((seekRequest.time.getTime() - fullTimeExtent.start.getTime()) / totalMs) * 100));

    let size = timeExtent[1] - timeExtent[0];
    if (showFullExtent || size >= 100 || size <= 0) {
      size = windowSizeRef.current;
      if (size >= 100 || size <= 0) size = getIntervalWindowPercent();
    }
    let start = Math.max(0, pct - size / 2);
    let end = Math.min(100, pct + size / 2);
    if (start === 0) end = Math.min(100, size);
    if (end === 100) start = Math.max(0, 100 - size);

    windowSizeRef.current = end - start;
    setTimeExtent([start, end]);
    setShowFullExtent(false);
    setIsPlaying(Boolean(seekRequest.play));
  }, [seekRequest, fullTimeExtent, timeExtent, showFullExtent, getIntervalWindowPercent]);

  // Convert percentage to date
  const getDateFromPercentage = useCallback((percentage: number): Date => {
    if (!fullTimeExtent) return new Date();
    const totalMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
    return new Date(fullTimeExtent.start.getTime() + (totalMs * percentage) / 100);
  }, [fullTimeExtent]);

  // Get visible data for current time window
  const getVisibleDataClientSide = useCallback(() => {
    if (!fullTimeExtent) return { points: [], routes: [] };

    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    // Determine the visible time window from timeExtent percentages
    const windowStart = getDateFromPercentage(timeExtent[0]);
    const windowEnd = getDateFromPercentage(timeExtent[1]);
    
    // Removed verbose logging - data filtering is working correctly
    
    const visiblePoints = dataPoints.filter(point => {
      // When showing full extent, skip time filtering
      if (!showFullExtent) {
        const pointTime = point.timestamp.getTime();
        const inWindow = pointTime >= windowStart.getTime() && pointTime <= windowEnd.getTime();
        if (!inWindow) return false;
      }
      
      // Always show co-traveler data (not filtered by device selection)
      if ((point as any).isCoTraveler) return true;
      
      // Apply device filtering for primary data
      if (selectedDeviceIds.length === 0) return true;
      // Normalize cellphone/smartphone/samsungphone -> "phone" to match the key
      // built by extractDevicesEfficiently in PathfinderPage.tsx. Without this,
      // any track whose raw device_type contains "phone" but isn't literally "phone"
      // (e.g. imported rows with "cellphone") is dropped from the map + charts even
      // though the sidebar counts it as loaded.
      const rawPtType = (point.deviceType || 'phone').toLowerCase();
      const normalizedPtType = rawPtType.includes('phone') ? 'phone' : rawPtType;
      const deviceKey = `${point.studentId}-${normalizedPtType}`;
      return selectedDeviceIds.includes(deviceKey);
    });

    // Filter routes by time - create progressive routes that only show points within the time window
    const visibleRoutes = routes
      .map(route => {
        // Always show co-traveler routes (not filtered by device selection)
        const isCoTravelerRoute = (route as any).isCoTravelerRoute;
        
        // Check device selection (skip for co-traveler routes)
        if (!isCoTravelerRoute && selectedDeviceIds.length > 0 && !selectedDeviceIds.includes(route.deviceId)) {
          return null;
        }
        
        // When showing full extent, show all route points
        if (showFullExtent) {
          return route; // Return entire route without time filtering
        }
        
        // Find all points within the time window
        const pointsInWindow = route.points.filter(point => {
          const pointTime = point.timestamp.getTime();
          return pointTime >= windowStart.getTime() && pointTime <= windowEnd.getTime();
        });
        
        // If no points in window, return null
        if (pointsInWindow.length === 0) {
          return null;
        }
        
        // Sort points by timestamp (ensure proper line drawing order)
        pointsInWindow.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        // Create segmented route with updated coordinates array
        return {
          ...route,
          points: pointsInWindow,
          coordinates: pointsInWindow.map(p => p.coordinates), // Important: update coordinates for line rendering
          startTime: pointsInWindow[0].timestamp,
          endTime: pointsInWindow[pointsInWindow.length - 1].timestamp
        };
      })
      .filter((route): route is PathfinderRoute => route !== null); // Remove null routes
    
    return { points: visiblePoints, routes: visibleRoutes };
  }, [dataPoints, routes, fullTimeExtent, selectedDevices, timeExtent, showFullExtent, getDateFromPercentage]);

  // Handle time update
  const scheduleOnTimeUpdate = useCallback((
    time: Date,
    points: PathfinderDataPoint[],
    routes: PathfinderRoute[],
    window: { start: Date; end: Date }
  ) => {
    onTimeUpdate(time, points, routes, showFullExtent, window);
  }, [onTimeUpdate, showFullExtent]);

  // Helper function to check if current time is within a dwell period
  const isInDwell = useCallback((currentTimeMs: number): { inDwell: boolean; dwellEnd?: number } => {
    if (!skipDwells || dwellLocations.length === 0) {
      return { inDwell: false };
    }
    
    for (const dwell of dwellLocations) {
      const start = parseBackendDateTime(dwell.start_time);
      const end = parseBackendDateTime(dwell.end_time);
      if (!start || !end) continue;
      const dwellStart = start.getTime();
      const dwellEnd = end.getTime();
      
      if (currentTimeMs >= dwellStart && currentTimeMs <= dwellEnd) {
        return { inDwell: true, dwellEnd };
      }
    }
    
    return { inDwell: false };
  }, [skipDwells, dwellLocations]);

  // Playback logic - advances the time window forward, skipping dwells if enabled
  useEffect(() => {
    if (isPlaying && fullTimeExtent) {
      const timerId = window.setInterval(() => {
        setTimeExtent(prev => {
          const windowSize = prev[1] - prev[0];
          const totalDurationMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
          
          // Calculate how much to advance (as percentage) based on interval
          let advancePercent = (getIntervalDurationMs(timeUnit, intervalValue) / totalDurationMs) * 100;
          
          let newStart = prev[0] + advancePercent;
          let newEnd = prev[1] + advancePercent;
          
          // If skip dwells is enabled, check if we're in a dwell and skip to the end of it
          if (skipDwells && dwellLocations.length > 0) {
            const currentTime = getDateFromPercentage((newStart + newEnd) / 2);
            const dwellCheck = isInDwell(currentTime.getTime());
            
            if (dwellCheck.inDwell && dwellCheck.dwellEnd) {
              // Skip to the end of the dwell
              const dwellEndPercent = ((dwellCheck.dwellEnd - fullTimeExtent.start.getTime()) / totalDurationMs) * 100;
              const skipAmount = dwellEndPercent - ((prev[0] + prev[1]) / 2);
              
              if (skipAmount > 0) {
                newStart = prev[0] + skipAmount;
                newEnd = prev[1] + skipAmount;
              }
            }
          }
          
          // Check if we've reached the end
          if (newEnd >= 100) {
            if (loop) {
              // Loop back to start with same window size
              return [0, windowSize];
            } else {
              setIsPlaying(false);
              return [100 - windowSize, 100];
            }
          }
          
          return [newStart, newEnd];
        });
      }, playRate * 1000);
      
      playbackTimerRef.current = timerId;

      return () => {
        if (playbackTimerRef.current) {
          clearInterval(playbackTimerRef.current);
        }
      };
    }
  }, [isPlaying, intervalValue, timeUnit, playRate, loop, fullTimeExtent, skipDwells, dwellLocations, isInDwell, getDateFromPercentage]);

  // Update visible data when time extent changes
  useEffect(() => {
    if (fullTimeExtent) {
      const visibleData = getVisibleDataClientSide();
      const currentTime = getDateFromPercentage((timeExtent[0] + timeExtent[1]) / 2); // Middle of window
      const windowStart = getDateFromPercentage(timeExtent[0]);
      const windowEnd = getDateFromPercentage(timeExtent[1]);
      scheduleOnTimeUpdate(currentTime, visibleData.points, visibleData.routes, { start: windowStart, end: windowEnd });
    }
  }, [timeExtent, getVisibleDataClientSide, scheduleOnTimeUpdate, fullTimeExtent, getDateFromPercentage]);

  // Handle slider change - update the time window
  const handleSliderChange = useCallback((_: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue) && newValue.length === 2) {
      setTimeExtent([newValue[0], newValue[1]]);
      setIsPlaying(false);
      
      // Update window size ref
      windowSizeRef.current = newValue[1] - newValue[0];
      
      // If user manually adjusts slider, exit full extent mode
      if (showFullExtent) {
        setShowFullExtent(false);
      }
    }
  }, [showFullExtent]);

  const handleFollowModeChange = useCallback((enabled: boolean) => {
    setFollowMode(enabled);
    if (onFollowModeChange) {
      onFollowModeChange(enabled);
    }
  }, [onFollowModeChange]);

  // Handler to adjust window size (widen/narrow the visible time span)
  const adjustWindowSize = useCallback((direction: 'narrower' | 'wider') => {
    setTimeExtent(prev => {
      const currentSize = prev[1] - prev[0];
      const center = (prev[0] + prev[1]) / 2;
      const intervalStep = getIntervalWindowPercent();
      
      // Adjust by the same interval selected for playback, so Seconds/30
      // can shrink the active window all the way down to 30 seconds.
      let newSize = direction === 'wider'
        ? Math.min(100, currentSize + intervalStep)
        : Math.max(intervalStep, currentSize - intervalStep);
      
      // Calculate new start and end, keeping center position
      let newStart = Math.max(0, center - newSize / 2);
      let newEnd = Math.min(100, center + newSize / 2);
      
      // Adjust if we hit boundaries
      if (newStart === 0) newEnd = Math.min(100, newSize);
      if (newEnd === 100) newStart = Math.max(0, 100 - newSize);
      
      windowSizeRef.current = newEnd - newStart;
      return [newStart, newEnd];
    });
    setShowFullExtent(false);
    setIsPlaying(false);
  }, [getIntervalWindowPercent]);

  // Handler to toggle full extent
  const toggleFullExtent = useCallback(() => {
    if (!showFullExtent) {
      // Switch TO full extent - save current window position first
      
      // Save current window size (in case user manually adjusted slider)
      const currentWindowSize = timeExtent[1] - timeExtent[0];
      if (currentWindowSize < 100) {
        windowSizeRef.current = currentWindowSize;
      }
      
      setTimeExtent([0, 100]);
      setShowFullExtent(true);
      setIsPlaying(false);
    } else {
      // Switch FROM full extent back to window mode
      const windowSize = windowSizeRef.current;
      
      // If windowSize is 100 or invalid, use a sensible default from interval settings
      let effectiveWindowSize = windowSize;
      if (windowSize >= 100 || windowSize <= 0) {
        // Recalculate from interval settings
        effectiveWindowSize = getIntervalWindowPercent();
        windowSizeRef.current = effectiveWindowSize;
      }
      
      setTimeExtent([0, effectiveWindowSize]);
      setShowFullExtent(false);
    }
  }, [showFullExtent, timeExtent, getIntervalWindowPercent]);

  // Format window size for display
  const formatWindowSize = useCallback((): string => {
    if (!fullTimeExtent) return 'N/A';
    const windowSize = timeExtent[1] - timeExtent[0];
    const totalMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
    const windowMs = (windowSize / 100) * totalMs;
    
    const seconds = windowMs / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;
    
    if (days >= 1) return `${days.toFixed(1)}d`;
    if (hours >= 1) return `${hours.toFixed(1)}h`;
    if (minutes >= 1) return `${minutes.toFixed(0)}m`;
    return `${seconds.toFixed(0)}s`;
  }, [timeExtent, fullTimeExtent]);

  if (!fullTimeExtent) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Load data to enable timeline playback
        </Typography>
      </Box>
    );
  }
  
  const windowStartTime = getDateFromPercentage(timeExtent[0]);
  const windowEndTime = getDateFromPercentage(timeExtent[1]);

  return (
    <Box sx={{ px: { xs: 0.75, sm: 1.5 }, py: 0.5 }}>
      {/* Main Controls - Responsive layout */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 0.25, sm: 0.5 },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}>
        {/* Left Controls Group: Window Size + Play */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.25,
          order: { xs: 1, md: 1 },
        }}>
          {/* Window Size Controls */}
          <IconButton
            onClick={() => adjustWindowSize('narrower')}
            size="small"
            disabled={showFullExtent}
            sx={{ width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 }, p: 0.25 }}
            title="Narrower window"
          >
            <BackIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          </IconButton>
          
          <Typography 
            variant="caption" 
            sx={{ 
              minWidth: { xs: 26, sm: 32 }, 
              textAlign: 'center', 
              fontSize: { xs: '0.55rem', sm: '0.6rem' },
              color: 'white',
              fontWeight: 'bold',
              bgcolor: showFullExtent ? '#4caf50' : '#ff9800',
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              border: showFullExtent ? '2px solid #2e7d32' : '2px solid #f57c00',
              boxShadow: showFullExtent ? '0 0 6px rgba(76, 175, 80, 0.4)' : 'none'
            }}
          >
            {showFullExtent ? 'ALL' : formatWindowSize()}
          </Typography>
          
          <IconButton
            onClick={() => adjustWindowSize('wider')}
            size="small"
            disabled={showFullExtent}
            sx={{ width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 }, p: 0.25 }}
            title="Wider window"
          >
            <ForwardIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          </IconButton>

          {/* Play/Pause Button */}
          <IconButton
            onClick={() => setIsPlaying(!isPlaying)}
            size="small"
            sx={{ 
              bgcolor: isPlaying ? 'error.light' : 'primary.light',
              '&:hover': { bgcolor: isPlaying ? 'error.main' : 'primary.main' },
              color: 'white',
              width: { xs: 26, sm: 28 },
              height: { xs: 26, sm: 28 },
              ml: 0.25,
            }}
          >
            {isPlaying ? <PauseIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : <PlayIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
          </IconButton>
        </Box>

        {/* Slider Section - Takes full width on mobile */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          order: { xs: 3, md: 2 },
          width: { xs: '100%', md: 'auto' },
          mt: { xs: 0.5, md: 0 },
        }}>
          {/* Window Start Time Display */}
          <Typography 
            variant="caption" 
            sx={{ 
              minWidth: { xs: 55, sm: 120 }, 
              fontSize: { xs: '0.55rem', sm: '0.65rem' }, 
              color: 'text.secondary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {windowStartTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>

          {/* Timeline Range Slider */}
          <Box sx={{ flexGrow: 1, position: 'relative', minWidth: { xs: 100, sm: 150 } }}>
            <Slider
              value={timeExtent}
              onChange={handleSliderChange}
              min={0}
              max={100}
              step={Math.max(0.001, Math.min(0.1, getIntervalWindowPercent()))}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => {
                const time = getDateFromPercentage(value);
                return time.toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              }}
              sx={{
                // Extra top padding keeps the hover time fully inside the
                // timeline card. Without it the label is clipped by the map.
                pt: '32px',
                pb: '10px',
                height: { xs: 4, sm: 6 },
                '& .MuiSlider-thumb': {
                  width: { xs: 14, sm: 16 },
                  height: { xs: 14, sm: 16 },
                },
                '& .MuiSlider-track': {
                  height: { xs: 4, sm: 6 },
                  border: 'none',
                  bgcolor: 'primary.main',
                },
                '& .MuiSlider-rail': {
                  height: { xs: 4, sm: 6 },
                  opacity: 0.3,
                },
                '& .MuiSlider-valueLabel': {
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  padding: '6px 10px',
                  borderRadius: '6px',
                  top: -6,
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Box>

          {/* Window End Time Display */}
          <Typography 
            variant="caption" 
            sx={{ 
              minWidth: { xs: 55, sm: 120 }, 
              textAlign: 'right', 
              fontSize: { xs: '0.55rem', sm: '0.65rem' }, 
              color: 'text.secondary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {windowEndTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>

        {/* Right Controls Group: Extent + Settings + Layers */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.25,
          order: { xs: 2, md: 3 },
          ml: { xs: 'auto', md: 0 },
        }}>
          {/* Full Extent Toggle */}
          <IconButton
            onClick={toggleFullExtent}
            size="small"
            sx={{ 
              width: { xs: 26, sm: 28 }, 
              height: { xs: 26, sm: 28 },
              p: 0.25,
              bgcolor: showFullExtent ? '#4caf50' : 'transparent',
              color: showFullExtent ? 'white' : 'inherit',
              border: showFullExtent ? '2px solid #2e7d32' : 'none',
              boxShadow: showFullExtent ? '0 0 6px rgba(76, 175, 80, 0.4)' : 'none',
              '&:hover': {
                bgcolor: showFullExtent ? '#45a049' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
            title={showFullExtent ? "Show trail mode" : "Show all data"}
          >
            <FullExtentIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          </IconButton>

          {/* Map Settings gear - opens the centered MapLayerControlsDialog
              (managed by PathfinderPage) which contains BOTH the map layer
              toggles and the playback settings. Splitting them across two
              floating panels caused UX issues; one dialog with a clear X
              button is simpler. */}
          <IconButton
            onClick={() => onOpenMapSettings?.()}
            size="small"
            sx={{ width: { xs: 26, sm: 28 }, height: { xs: 26, sm: 28 }, p: 0.25 }}
            title="Map Settings (layers & playback)"
          >
            <SettingsIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile: Time range display below controls */}
      <Box sx={{ 
        display: { xs: 'flex', sm: 'none' }, 
        justifyContent: 'space-between',
        mt: 0.5,
        px: 1,
      }}>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
          {windowStartTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
          {windowEndTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      {/* Consolidated Map Settings dialog - holds BOTH map layer toggles
          and playback settings. Mounted here (rather than inside
          PathfinderMapGL) because the playback state lives in this
          component. PathfinderPage owns the open/close boolean so the
          same dialog can be triggered from the map's toolbar button
          as well as the slider's gear above. MUI Portals the dialog
          into document.body so position is fine regardless of where in
          the tree it's declared. */}
      <MapLayerControlsDialog
        open={mapSettingsOpen}
        onClose={() => onMapSettingsClose?.()}
        playbackOnly={playbackOnly}
        // Primary layers
        showDataPoints={showDataPoints}
        onShowDataPointsChange={onShowDataPointsChange}
        showRouteLines={showRouteLines}
        onShowRouteLinesChange={onShowRouteLinesChange}
        showDirectionArrows={showDirectionArrows}
        onShowDirectionArrowsChange={onShowDirectionArrowsChange}
        showHeatmap={showHeatmap}
        onShowHeatmapChange={onShowHeatmapChange}
        enableClustering={enableClustering}
        onEnableClusteringChange={onEnableClusteringChange}
        showDwells={showDwells}
        onShowDwellsChange={onShowDwellsChange}
        showPttMessages={showPttMessages}
        onShowPttMessagesChange={onShowPttMessagesChange}
        clusterPttMessages={clusterPttMessages}
        onClusterPttMessagesChange={onClusterPttMessagesChange}
        showDwellLabels={showDwellLabels}
        onShowDwellLabelsChange={onShowDwellLabelsChange}
        dwellCount={dwellCount}
        showItinerary={showItinerary}
        onShowItineraryChange={onShowItineraryChange}
        hasItineraryData={hasItineraryData}
        showCameras={showCameras}
        onShowCamerasChange={onShowCamerasChange}
        cameraCount={cameraCount}
        cameraControls={cameraControls}
        onZoomToBounds={onZoomToBounds}
        // Co-Traveler
        showCoTravelerPoints={showCoTravelerPoints}
        onShowCoTravelerPointsChange={onShowCoTravelerPointsChange}
        showCoTravelerLines={showCoTravelerLines}
        onShowCoTravelerLinesChange={onShowCoTravelerLinesChange}
        showCoTravelerHeatmap={showCoTravelerHeatmap}
        onShowCoTravelerHeatmapChange={onShowCoTravelerHeatmapChange}
        highlightClosestSrp={highlightClosestSrp}
        onHighlightClosestSrpChange={onHighlightClosestSrpChange}
        // White Cell
        whiteCellInterval={whiteCellInterval}
        onWhiteCellIntervalChange={onWhiteCellIntervalChange}
        // Playback (local state)
        timeUnit={timeUnit}
        onTimeUnitChange={setTimeUnit}
        intervalValue={intervalValue}
        onIntervalValueChange={setIntervalValue}
        playRate={playRate}
        onPlayRateChange={setPlayRate}
        loop={loop}
        onLoopChange={setLoop}
        followMode={followMode}
        onFollowModeChange={handleFollowModeChange}
        skipDwells={skipDwells}
        onSkipDwellsChange={setSkipDwells}
        hideGaps={hideGaps}
        onHideGapsChange={onHideGapsChange}
        hideImplausibleMovement={hideImplausibleMovement}
        onHideImplausibleMovementChange={onHideImplausibleMovementChange}
        implausibleMaxSpeedKmh={implausibleMaxSpeedKmh}
        onImplausibleMaxSpeedKmhChange={onImplausibleMaxSpeedKmhChange}
        onUseOrgTrackFilterDefaults={onUseOrgTrackFilterDefaults}
        trackFiltersUserCustomized={trackFiltersUserCustomized}
      />
    </Box>
  );
};

export default CompactTimeSlider;

